import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { ShoppingBag, TrendingUp, Clock, MessageSquare } from "lucide-react";
import api from "../../lib/api";
import { formatKobo } from "../../lib/format";

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [recent, setRecent] = useState([]);

  const load = useCallback(() => {
    api.get("/admin/stats").then(({ data }) => setStats(data)).catch(() => {});
    api.get("/admin/orders").then(({ data }) => setRecent(data.slice(0, 6))).catch(() => {});
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 20000);
    return () => clearInterval(t);
  }, [load]);

  const cards = [
    { label: "Orders today", value: stats?.orders_today ?? "—", icon: ShoppingBag, color: "bg-sapore-purple", to: "/admin/orders?metric=orders_today" },
    { label: "Paid today", value: stats ? formatKobo(stats.paid_today) : "—", icon: TrendingUp, color: "bg-sapore-gold text-sapore-ink", to: "/admin/orders?metric=paid_today" },
    { label: "Open orders", value: stats?.open_orders ?? "—", icon: Clock, color: "bg-sapore-purpleLight", to: "/admin/orders?metric=open" },
    { label: "New enquiries", value: stats?.new_enquiries ?? "—", icon: MessageSquare, color: "bg-sapore-purpleDark", to: "/admin/enquiries" },
  ];

  return (
    <div>
      <h1 className="font-serif text-3xl font-bold text-sapore-purple mb-6">Dashboard</h1>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        {cards.map((c) => (
          <Link key={c.label} to={c.to} data-testid={`stat-${c.label.toLowerCase().replace(/[^a-z]+/g, "-")}`} className="bg-white rounded-2xl border border-sapore-border p-5 block hover:-translate-y-0.5 hover:shadow-lg transition-all">
            <div className={`w-11 h-11 rounded-xl ${c.color} text-white flex items-center justify-center mb-3`}>
              <c.icon size={20} />
            </div>
            <p className="text-2xl font-bold text-sapore-ink">{c.value}</p>
            <p className="text-sm text-sapore-muted">{c.label}</p>
          </Link>
        ))}
      </div>

      <div className="bg-white rounded-2xl border border-sapore-border p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-serif text-xl text-sapore-purple">Recent orders</h2>
          <Link to="/admin/orders" className="text-sm text-sapore-purple hover:text-sapore-gold font-medium">View all</Link>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-sapore-muted border-b border-sapore-border">
                <th className="py-2 pr-4">Reference</th><th className="py-2 pr-4">Customer</th><th className="py-2 pr-4">Total</th><th className="py-2">Status</th>
              </tr>
            </thead>
            <tbody>
              {recent.map((o) => (
                <tr key={o.id} className="border-b border-sapore-border/60">
                  <td className="py-2.5 pr-4 font-medium">{o.reference}</td>
                  <td className="py-2.5 pr-4">{o.customer?.name}</td>
                  <td className="py-2.5 pr-4">{formatKobo(o.total)}</td>
                  <td className="py-2.5"><span className="text-xs px-2 py-0.5 rounded-full bg-sapore-goldSubtle text-sapore-ink">{o.status}</span></td>
                </tr>
              ))}
              {recent.length === 0 && <tr><td colSpan={4} className="py-8 text-center text-sapore-muted">No orders yet.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
